import {
  MEAL_ROLES,
  type AdminFoodDetail,
  type AdminServingPolicyRequest,
  type MealRoleName,
} from '@steado/admin-contract/foods';
import { useMutation } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/lib/auth/auth-context';
import { humanise } from '@/lib/format';
import { Field, Fields, Panel } from '@/components/panel/panels';

import { updateFood } from '@/screens/foods/foods-query';

/**
 * What anchors a meal, and how many household portions the plan solver may
 * serve of this food — the two curated facts `serving-policy.ts`'s resolver
 * reads, edited together because the second only makes sense in light of the
 * first (a role default table has no opinion on an uncurated food, and the
 * override's own numbers are judged against whatever role is set).
 */

interface ServingPolicyPanelProps {
  readonly food: AdminFoodDetail;
  readonly canMutate: boolean;
  readonly onMutated: (food: AdminFoodDetail) => void;
}

/** The Select's own vocabulary can't carry `null`, so uncurated gets a sentinel absent from `MEAL_ROLES`. */
const UNCURATED = 'uncurated' as const;

export function ServingPolicyPanel({ food, canMutate, onMutated }: ServingPolicyPanelProps) {
  const { api } = useAuth();
  const [editingOverride, setEditingOverride] = useState(false);

  const setMealRole = useMutation({
    mutationFn: (mealRole: MealRoleName | null) => updateFood(api, food.id, { mealRole }),
    onSuccess: onMutated,
    retry: false,
  });

  const saveOverride = useMutation({
    mutationFn: (servingPolicy: AdminServingPolicyRequest) => updateFood(api, food.id, { servingPolicy }),
    onSuccess: (updated) => {
      setEditingOverride(false);
      onMutated(updated);
    },
    retry: false,
  });

  const clearOverride = useMutation({
    mutationFn: () => updateFood(api, food.id, { servingPolicy: null }),
    onSuccess: onMutated,
    retry: false,
  });

  const roleId = useId();
  const isBusy = setMealRole.isPending || saveOverride.isPending || clearOverride.isPending;

  return (
    <Panel
      title="Serving policy"
      description="How many household portions the plan solver may serve of this food, and what it anchors a meal with."
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={roleId}>Meal role</Label>
          <Select
            value={food.mealRole ?? UNCURATED}
            disabled={!canMutate || isBusy}
            onValueChange={(next) => {
              setMealRole.mutate(next === UNCURATED ? null : (next as MealRoleName));
            }}
          >
            <SelectTrigger id={roleId} className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCURATED}>Uncurated</SelectItem>
              {MEAL_ROLES.map((role) => (
                <SelectItem key={role} value={role}>
                  {humanise(role)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Label>In effect now</Label>
            <Badge tone={food.servingPolicy === null ? 'mute' : 'accent'}>
              {food.servingPolicy === null ? 'role default' : 'override'}
            </Badge>
          </div>
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            {food.servingPolicy === null
              ? food.mealRole === null
                ? 'No role and no override — the solver falls back to the raw unit grid.'
                : `No override — the "${humanise(food.mealRole)}" role default applies.`
              : 'A curated override for this food, replacing whatever the role default would say.'}
          </p>
          <Fields>
            <Field label="Min" value={String(food.effectiveServingPolicy.minPortions)} mono />
            <Field label="Typical" value={String(food.effectiveServingPolicy.typicalPortions)} mono />
            <Field label="Max" value={String(food.effectiveServingPolicy.maxPortions)} mono />
            <Field
              label="Max per day"
              value={
                food.effectiveServingPolicy.maxDayPortions === null
                  ? 'none'
                  : String(food.effectiveServingPolicy.maxDayPortions)
              }
              mono
            />
            <Field
              label="Elastic"
              value={food.effectiveServingPolicy.elastic ? 'yes' : 'no — locked at typical'}
            />
          </Fields>
        </div>

        {editingOverride ? (
          <ServingPolicyEditor
            initial={food.servingPolicy}
            isPending={saveOverride.isPending}
            error={saveOverride.isError ? saveOverride.error.message : null}
            onSubmit={(policy) => {
              saveOverride.mutate(policy);
            }}
            onCancel={() => {
              setEditingOverride(false);
              saveOverride.reset();
            }}
          />
        ) : canMutate ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={isBusy}
              onClick={() => {
                setEditingOverride(true);
              }}
            >
              {food.servingPolicy === null ? 'Set override' : 'Edit override'}
            </Button>
            {food.servingPolicy === null ? null : (
              <Button
                variant="ghost"
                size="sm"
                className="text-err-fg hover:bg-err-bg hover:text-err-fg"
                disabled={isBusy}
                onClick={() => {
                  clearOverride.mutate();
                }}
              >
                Clear override
              </Button>
            )}
          </div>
        ) : null}

        {setMealRole.isError ? (
          <p role="alert" className="text-[13px] text-err-fg">
            {setMealRole.error.message}
          </p>
        ) : null}
        {clearOverride.isError ? (
          <p role="alert" className="text-[13px] text-err-fg">
            {clearOverride.error.message}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}

interface ServingPolicyEditorProps {
  readonly initial: AdminFoodDetail['servingPolicy'];
  readonly isPending: boolean;
  readonly error: string | null;
  readonly onSubmit: (policy: AdminServingPolicyRequest) => void;
  readonly onCancel: () => void;
}

function ServingPolicyEditor({ initial, isPending, error, onSubmit, onCancel }: ServingPolicyEditorProps) {
  const minId = useId();
  const typicalId = useId();
  const maxId = useId();
  const dayId = useId();
  const elasticId = useId();

  const [min, setMin] = useState(initial === null ? '0.5' : String(initial.minPortions));
  const [typical, setTypical] = useState(initial === null ? '1' : String(initial.typicalPortions));
  const [max, setMax] = useState(initial === null ? '2' : String(initial.maxPortions));
  const [maxDay, setMaxDay] = useState(initial?.maxDayPortions == null ? '' : String(initial.maxDayPortions));
  const [elastic, setElastic] = useState(initial?.elastic ?? true);

  const parsedMin = Number(min);
  const parsedTypical = Number(typical);
  const parsedMax = Number(max);
  const parsedMaxDay = maxDay.trim() === '' ? null : Number(maxDay);

  const valid =
    Number.isFinite(parsedMin) &&
    parsedMin > 0 &&
    Number.isFinite(parsedTypical) &&
    Number.isFinite(parsedMax) &&
    parsedMin <= parsedTypical &&
    parsedTypical <= parsedMax &&
    (parsedMaxDay === null || (Number.isFinite(parsedMaxDay) && parsedMaxDay >= parsedMax));

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border bg-background p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={minId}>Min</Label>
          <Input
            id={minId}
            value={min}
            inputMode="decimal"
            onChange={(event) => {
              setMin(event.target.value);
            }}
            className="w-20 text-right font-mono"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={typicalId}>Typical</Label>
          <Input
            id={typicalId}
            value={typical}
            inputMode="decimal"
            onChange={(event) => {
              setTypical(event.target.value);
            }}
            className="w-20 text-right font-mono"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={maxId}>Max</Label>
          <Input
            id={maxId}
            value={max}
            inputMode="decimal"
            onChange={(event) => {
              setMax(event.target.value);
            }}
            className="w-20 text-right font-mono"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={dayId}>Max per day</Label>
          <Input
            id={dayId}
            value={maxDay}
            inputMode="decimal"
            placeholder="none"
            onChange={(event) => {
              setMaxDay(event.target.value);
            }}
            className="w-24 text-right font-mono"
          />
        </div>
        <div className="flex h-9 items-center gap-2">
          <Checkbox
            id={elasticId}
            checked={elastic}
            onCheckedChange={(checked) => {
              setElastic(checked === true);
            }}
          />
          <Label htmlFor={elasticId}>Elastic</Label>
        </div>
      </div>

      <p className="text-[13px] leading-relaxed text-muted-foreground">
        Portions of this food&rsquo;s own default unit. Min ≤ typical ≤ max, and max per day (when set) must
        be at least max. Unticking elastic locks every placement at typical — the solver never negotiates it
        against a calorie target, the right setting for a beverage, condiment or dessert.
      </p>

      <div className="flex items-center gap-2">
        <Button
          disabled={!valid || isPending}
          onClick={() => {
            onSubmit({
              minPortions: parsedMin,
              typicalPortions: parsedTypical,
              maxPortions: parsedMax,
              maxDayPortions: parsedMaxDay,
              elastic,
            });
          }}
        >
          Save
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        {error === null ? null : (
          <p role="alert" className="text-[13px] text-err-fg">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
